/**
 * RYFE backend client (Features #1–#6 surface).
 * Thin typed wrapper over fetch. Always sends credentials so the server-side
 * session cookie (ryfe_session) rides along. Throws ApiError on non-2xx.
 */

import { apiUrl } from "./config";

export interface Branding {
  productName: string;
  logo: string;
  favicon: string;
  wikiUrl: string;
  themeColor: string;
  poweredByExcalidraw: boolean;
  // V1.2 Block 8 — instance theming (server-validated; unset → defaults).
  theme?: {
    colors?: Record<string, string | null>;
    font?: string | null;
    dark?: Record<string, string | null> | null;
  } | null;
}

export interface RyfeUser {
  id: string;
  email: string;
  // V1.2 Block 3 — opaque, prefix-less Freigabe-ID (e.g. 7f3a-9c21-4d80).
  shareId?: string;
  isInstanceAdmin?: boolean;
}

// V1.2 Blocks 2/4 — an ACL entry as the sharing dialog consumes it.
export interface AclEntry {
  id: string;
  principalType: string;
  principalId: string;
  role: "owner" | "editor" | "viewer";
  label: string;
  createdAt?: string;
  // inherited entries are displayed but managed at their source folder
  inherited?: boolean;
  via?: string | null;
}

// R4 (Punkt 8.2) — eine ausstehende E-Mail-Einladung (Adresse ohne Konto):
// immer sichtbar und widerrufbar; wird bei Registrierung zum echten Eintrag.
export interface PendingInvite {
  id: string;
  email: string;
  role: "viewer" | "editor";
  created_at?: string;
}

// Revision R3 — ein Eintrag der zentralen Freigaben-Übersicht (/shares):
// AclEntry plus das Ziel-Objekt (Datei oder Ordner), an dem er hängt.
export interface ShareOverviewEntry extends AclEntry {
  target: {
    type: "file" | "folder";
    id: string;
    name: string;
    kind?: string;
    isRoot?: boolean;
  };
}

// V1.2 Block 5 — one hit in "Für mich freigegeben" (cross-workspace).
export interface SharedItem {
  kind: "folder" | "file";
  id: string;
  name: string;
  fileKind?: string;
  role: string;
  workspaceId: string;
  workspaceName: string;
}

export interface RyfeWorkspace {
  id: string;
  name: string;
  slug: string;
  role: "owner" | "admin" | "member" | "viewer";
}

// v1.8 Cluster B.5 (D-146) — Privatbereich: der Cross-WS-Überblick. Aufgaben
// tragen `responsible_user_id`; hier interessieren Titel/Status/Fälligkeit + der
// Workspace-Kontext. Termine/Zeitblöcke/Tag-Typen werden defensiv gerendert.
export interface PrivateTask {
  id: string;
  title?: string | null;
  name?: string | null;
  status?: string | null;
  due_date?: string | null;
  workspace_id?: string | null;
  [key: string]: unknown;
}
export interface PrivateOverview {
  workspaces: RyfeWorkspace[];
  tasks: PrivateTask[];
  appointments: { id: string; [key: string]: unknown }[];
  time_blocks: { id: string; [key: string]: unknown }[];
  tag_types: { id: string; name?: string; [key: string]: unknown }[];
}

// v1.8 Cluster B.5 (D-146) — die Cross-WS-Aggregation der Systemlisten für den
// Privatbereich. Je Entität eine Anzahl + Top-N Beispiel-Einträge, jeder mit
// Workspace-Herkunft (id + name). GET /api/private/systemlists.
export interface PrivateBucket<T> {
  count: number;
  items: T[];
}
export interface PrivateSystemlists {
  workspaces: RyfeWorkspace[];
  entities: {
    tasks: PrivateBucket<{
      id: string;
      name: string | null;
      status: string | null;
      due_date: string | null;
      workspace_id: string | null;
      workspace_name: string | null;
    }>;
    projects: PrivateBucket<{
      id: string;
      name: string | null;
      status: string | null;
      workspace_id: string;
      workspace_name: string;
    }>;
    contacts: PrivateBucket<{
      id: string;
      display_name: string | null;
      kind: string | null;
      workspace_id: string;
      workspace_name: string;
    }>;
    bookmarks: PrivateBucket<{
      id: string;
      title: string | null;
      url: string | null;
      workspace_id: string;
      workspace_name: string;
    }>;
    appointments: PrivateBucket<{
      id: string;
      title: string | null;
      start_ts: string | null;
      end_ts: string | null;
      status: string | null;
      contact_id: string | null;
      workspace_id: string | null;
      workspace_name: string | null;
    }>;
  };
}

// v1.8 WS-Plattform (D-111/D-132) — a group is a sub-scope of a workspace.
export interface RyfeGroup {
  id: string;
  workspace_id: string;
  name: string;
  team_folder_id: string | null;
  // On create the backend also returns the canonical KI-Ordner (D-134).
  ki_ordner_id?: string;
  member_count?: number;
  created_at?: string;
}

// Eine offene Workspace-Einladung an die eigene E-Mail (zum Annehmen/Ablehnen).
export interface WsInvite {
  id: string;
  workspace_id: string;
  workspace_name: string;
  role: string;
  created_at?: string;
}

// Workspace membership (System 1) — reused as the group-member candidate pool.
export interface WsMember {
  id: string;
  email: string;
  role: "owner" | "admin" | "member" | "viewer";
  created_at?: string;
}

// A group membership row (group role admin|member) resolved to the user's email.
export interface GroupMember {
  user_id: string;
  email: string;
  role: "admin" | "member";
}

// v1.8 AP-10 (D-128) — a capability grant (group × systemlist-type).
export interface Capability {
  id: string;
  workspace_id: string;
  group_id: string;
  list_type: string;
  can_read: boolean;
  can_submit: boolean;
  can_row_edit: boolean;
  can_schema_edit: boolean;
  field_edit: string[];
}

// v1.8 AP-5 (P23) — a „zuletzt verwendeter Ordner" row.
export interface RecentFolder {
  id: string;
  name: string;
  role?: "trash" | "archive" | null;
  scope_group_id?: string | null;
  last_opened_at?: string | null;
}

export type AclRole = "owner" | "editor" | "viewer";

export interface FolderChild {
  id: string;
  name: string;
  is_root?: boolean;
  // v1.3.0 Block 3 — "trash" | "archive" bei den beiden System-Ordnern.
  role?: "trash" | "archive" | null;
  created_at?: string;
  mcp_token_count?: number;
  shared_count?: number;
  my_role?: AclRole | null;
  // AP2·P3-4 — KI-Ordner type + optional workspace binding.
  ki_ordner_type?: "ws_bound" | "free";
  bound_workspace_id?: string | null;
  // AP2 Phase 5 (v1.9) — explicitly marked as a KI-Ordner (subtype).
  is_ki_ordner?: boolean;
}

export interface FileMeta {
  id: string;
  name: string;
  kind: string;
  // CC_11 (D-62) — 'template' marks a file as a Vorlage; null/undefined = none.
  role?: string | null;
  // v1.8 Weg 2 (D-109) — bei einer gebundenen Systemliste der Typ
  // `<paket>.<liste>` (z. B. `crm.kontakte`); null bei gewöhnlichen Listen.
  list_type?: string | null;
  // v1.6/D-KI — system files (e.g. the „Lenses" list) live on dedicated pages
  // and are hidden from + protected in the generic file browser.
  system?: boolean;
  content_type?: string | null;
  size_bytes?: number;
  folder_id?: string;
  // R4 — im flachen „Ordner ausblenden"-Modus mitgeliefert: Name des Ordners,
  // in dem die Datei liegt (als Herkunfts-Hinweis in der Namenszelle).
  folder_name?: string;
  created_at?: string;
  updated_at?: string;
  last_opened_at?: string | null;
  // AP-4 (P11.4) — the two auto system columns „erstellt von" / „zuletzt
  // bearbeitet von", resolved to the user's email (derived file metadata).
  created_by_email?: string | null;
  updated_by_email?: string | null;
  main_frame_id?: string | null; // Block B.2 — scene → main-frame pointer
  // V1.2 Block 9 — boards that embed this scene (reverse index scene_links).
  embedded_in?: { id: string; name: string }[];
}

export interface FolderListing {
  id: string;
  name?: string;
  // v1.3.0 Block 3 — `role` = dieser Ordner IST Papierkorb/Archiv,
  // `in_role` = er liegt darin (steuert das Kontextmenü).
  role?: "trash" | "archive" | null;
  in_role?: "trash" | "archive" | null;
  my_role?: AclRole | null;
  // CC_11-Revision — Breadcrumb-Pfad root → … → dieser Ordner.
  path?: {
    id: string;
    name: string;
    is_root?: boolean;
    role?: string | null;
  }[];
  folders: FolderChild[];
  files: FileMeta[];
}

export interface McpToken {
  id: string;
  name: string;
  folder_id: string;
  folder_name?: string;
  role: AclRole | null;
  expires_at: string | null;
  created_at: string;
  last_used_at: string | null;
  // v1.6 / D-KI (AP1) — archived tokens are hidden from the default list.
  archived_at?: string | null;
  status: "active" | "expired" | "archived";
}

// v1.6 / D-KI (AP4) — a workspace-shared custom column on „Meine Dateien".
export interface FileColumn {
  id: string;
  name: string;
  type: string;
  config: Record<string, unknown>;
  position: number;
  private: boolean;
  created_by?: string | null;
  // v1.7 — the folder this column is scoped to (visible in it + its subtree).
  folder_id?: string;
}

// v1.6 / D-KI (AP-KI-Tags) — a document's category tag + free-form metadata
// note. `categories` is the closed set the server offers for the tag.
export interface DocumentMetadata {
  file_id: string;
  category: string | null;
  metadata_md: string;
  updated_at: string | null;
  categories: string[];
}

// The slim per-file lookup for the file list: the tag + whether a note exists.
export interface FileCategory {
  category: string | null;
  has_md: boolean;
}

export interface LibraryFolderRow {
  id: string;
  name: string;
  parent_id: string | null;
  created_at?: string;
  item_count?: number;
}

export interface LibraryItemRow {
  id: string;
  name: string;
  folder_id: string | null;
  element_count: number;
  source_package?: string | null;
  license?: string | null;
  created_at?: string;
}

export interface LibraryListing {
  id?: string;
  name?: string;
  parent_id?: string | null;
  // v1.3.0 Block 2 (D-17) — the two fixed L1 roles and whether this subtree is
  // display-only (everything below system_root is).
  role?: "system_root" | "my_root" | null;
  read_only?: boolean;
  my_role?: string | null;
  can_write?: boolean;
  folders: LibraryFolderRow[];
  items: LibraryItemRow[];
}

export interface LibraryItemFull extends LibraryItemRow {
  elements: unknown[];
}

// CC_05 Block 5 — a persisted table-filter preset. `config` is a FilterConfig
// (table-filter.ts); the server stores it opaquely.
export interface SavedView {
  id: string;
  scope: string;
  name: string;
  config: unknown;
  created_at?: string;
}

// CC_05 Block 8 — list schema + rows.
export type ListColumnType =
  | "text"
  | "number"
  | "checkbox"
  | "date"
  | "select"
  | "url"
  // url_text + color: Render-Hälfte in CC_09 (P22); persistierbar (Backend-
  // Allowlist + DB-type-CHECK) erst mit Migration 019 in CC_11.
  | "url_text"
  | "color"
  // CC_11 Block 6 (D-56) — config-only column: per-column outbound hooks.
  | "webhooks"
  | "uuid"
  | "relation";
export interface ListColumn {
  id: string;
  file_id: string;
  name: string;
  type: ListColumnType;
  position: number;
  config: Record<string, unknown>;
  created_at?: string;
  // v1.6 / D-KI — a locked system column (value editable, column not deletable).
  locked?: boolean;
}
export interface ListRow {
  id: string;
  file_id: string;
  data: Record<string, unknown>;
  position: number;
  // v1.6 / D-KI — system-list rows carry their creator + privacy (a private
  // lens is visible only to its creator).
  created_by?: string | null;
  private?: boolean;
  // CC_07 Block 2 (D-41) — stable per-list row number; exposed read-only in the
  // FE as the "row_id" system column. Gaps allowed, never re-assigned.
  seq?: number;
  created_at?: string;
  // R4 — Unterwert-Baum: Eltern-Zeile (null = oberste Ebene L1).
  parent_row_id?: string | null;
  // v1.8 AP-10 Inc 3b (D-140) — Ersteller-Gruppe einer Systemlisten-Zeile;
  // null = WS-weiter Eintrag. Nur für Systemlisten gesetzt.
  group_id?: string | null;
  links: Record<string, string[]>;
}
export interface ListSnapshot {
  columns: ListColumn[];
  rows: ListRow[];
  // R4 — Listen-Einstellungen, die mit dem Snapshot reisen.
  // `bound` = an eine Backing-Tabelle gebundene Systemliste (Weg 2, feste
  // Spalten); `can_create` = ob eine Zeile direkt anlegbar ist (false z. B. bei
  // Aufgaben/Kontakten, die anderswo entstehen).
  settings?: {
    allow_subvalues?: boolean;
    bound?: boolean;
    can_create?: boolean;
    // Redesign Welle R — die Liste erlaubt zusätzliche CUSTOM-Spalten („Achse",
    // Link/URL/Text …) über die festen Adapter-Spalten; steuert „+ Spalte".
    custom_columns?: boolean;
    // A2 (D-159) — Baum über eine Self-Relation-Spalte (z. B. `parent_process_id`);
    // die Spaltenwerte sind Zeilen-IDs.
    tree_parent_col?: string;
    // A3c (D-158) — gebundene Kontakte-Liste: das Zeilen-Detail zeigt ein
    // Adress-Subgrid (CRUD über die contacts/:id/addresses-Endpunkte). Die
    // Zeilen-ID ist die contact_id.
    detail_addresses?: boolean;
    // D-146/Team — gesetzt, wenn die Liste über `?group=<id>` auf genau eine
    // Gruppe verengt wurde (Team-View).
    group_filter?: string;
    // AP1 — die Liste legt Zeilen NICHT direkt an, sondern per Prozess-
    // Materialisierung (Aufgaben): das FE zeigt „＋ aus Prozess".
    materialize?: boolean;
    // Redesign Welle R — eine gebundene Liste, deren Zeilen Pflichtfelder haben
    // (z. B. Prozesse: `name`), liefert die Anlage-Felder; „+ Zeile" öffnet dann
    // ein Anlage-Formular statt eine leere Zeile zu posten (die 400en würde).
    create_fields?: {
      id: string;
      label: string;
      type: "text" | "select" | "textarea" | "date" | "rrule";
      options?: string[];
      required?: boolean;
      // Redesign Welle R — konditionales Feld: nur anzeigen/erfassen, wenn ein
      // anderes Feld den Wert hat (z. B. RRULE/Start nur bei type=iterativ).
      showIf?: { field: string; equals: string };
    }[];
  };
}

// D-146/Team — „Forms & View je Team": die departmentalen Systemlisten einer
// Gruppe (je gruppen-gefiltert öffenbar über getList(..., group_filter)) + die
// on-demand-Prozesse der Gruppe (instanziierbare Vorlagen).
export interface TeamLists {
  group: { id: string; name: string };
  lists: {
    list_type: string;
    name: string;
    file_id: string;
    // Der bei Gruppen-Anlage provisionierte Team-View (group_filter), oder null;
    // die Liste ist auch ohne gespeicherten View über getList(..., group)
    // gruppen-gefiltert öffenbar.
    view_id: string | null;
    group_filter: string;
  }[];
  // D-146/Team — die an DIESE Gruppe gebundenen Formulare der departmentalen
  // Listen (config.group_id = Gruppe). Verlinken über /f/:form_id (Submit
  // stempelt serverseitig via config.group_id). Leer, wenn (noch) keine da.
  forms: {
    form_id: string;
    name: string;
    list_type: string;
    file_id: string;
  }[];
  processes: {
    id: string;
    name: string;
    level: number | null;
    standalone: boolean | null;
  }[];
}

// D-147/D-148 — ein Pflicht-Input eines Prozesses für die Instanziierung. `field`
// ist eines der instanziierbaren Task-Felder; `contact_type` ist ein FE-Picker-
// Hinweis (der Server prüft nur die Präsenz).
export interface ProcessRequiredInput {
  field: string;
  label?: string | null;
  contact_type?: string | null;
}
// GET /api/workspaces/:ws/processes/:id — inkl. der konfigurierten Pflicht-Inputs.
export interface ProcessDetail {
  id: string;
  name: string;
  level: number | null;
  standalone: boolean | null;
  required_inputs: ProcessRequiredInput[];
  [key: string]: unknown;
}

// A3c (D-158) — eine Adresse eines Kontakts (Detail-Panel-Subgrid).
export interface Address {
  id: string;
  workspace_id?: string;
  contact_id: string;
  kind: string;
  street: string | null;
  zip: string | null;
  city: string | null;
  country: string | null;
  notes: string | null;
  created_at?: string;
  updated_at?: string | null;
}

// --- CC_11 (D-59/D-66): list views + forms ---------------------------------
export interface ListViewField {
  column_id: string;
  visible: boolean;
  required: boolean;
  order: number;
  prefill_default?: unknown;
  // R4 — kuratierte Relations-Auswahl + Schalter (nur bei Relations-Spalten).
  relation_options?: { row_id: string; display: string }[];
  relation_multiple?: boolean;
  relation_live_search?: boolean;
}
export interface ListView {
  id: string;
  file_id: string;
  // Open discriminator like files.kind — 'table' | 'form' | future.
  type: string;
  name: string;
  config: {
    form_id?: string;
    public_submit?: boolean;
    fields?: ListViewField[];
    [key: string]: unknown;
  };
  position: number;
  created_at?: string;
}
// R4 — eine kuratierte Relations-Option: eine vom Formular-Ersteller
// freigegebene Ziel-Zeile (id + Anzeigename).
export interface RelationOption {
  row_id: string;
  display: string;
}
export interface FormDescriptorField {
  column_id: string;
  name: string;
  type: string;
  options?: unknown[];
  // R4 — Relations-Felder: kuratierte Auswahl + abgeleitete Mehrfachauswahl +
  // ob dem angemeldeten Absender die guard-gefilterte Live-Suche offensteht.
  relation_options?: RelationOption[];
  relation_multiple?: boolean;
  relation_live_search?: boolean;
  required: boolean;
  prefill_default?: unknown;
}
export interface FormDescriptor {
  formId: string;
  name: string;
  listName: string;
  publicSubmit: boolean;
  fields: FormDescriptorField[];
}

// --- CC_11 (D-71): row comments --------------------------------------------
export interface RowComment {
  id: string;
  row_id: string;
  user_id: string | null;
  body: string;
  created_at: string;
  updated_at: string | null;
  author_email: string | null;
}

// --- CC_11 (D-62/D-63): templates ------------------------------------------
export interface TemplateFile {
  id: string;
  name: string;
  kind: string;
  folder_id: string;
  content_type: string | null;
  size_bytes: number;
  created_at: string;
  updated_at: string;
}

// --- CC_11 (D-72): per-user global settings ---------------------------------
export interface UserSettings {
  columnResizeIsolated: boolean;
  [key: string]: unknown;
}

export class ApiError extends Error {
  status: number;
  // Structured fields the server may add to an error body (e.g. a coded 409 like
  // CONFIRM_REQUIRED with a `count`); undefined when the body carried none.
  code?: string;
  count?: number;
  data?: Record<string, unknown>;
  constructor(status: number, message: string, data?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    if (data && typeof data.code === "string") {
      this.code = data.code;
    }
    if (data && typeof data.count === "number") {
      this.count = data.count;
    }
  }
}

async function request<T>(
  method: string,
  path: string,
  opts: {
    json?: unknown;
    raw?: BodyInit;
    headers?: Record<string, string>;
    // Force how the response body is read. Needed for file blobs, whose stored
    // content-type may itself be application/json (a board scene) — those must
    // still be returned as raw bytes, not parsed as a JSON object.
    responseType?: "auto" | "arraybuffer";
  } = {},
): Promise<T> {
  const headers: Record<string, string> = { ...opts.headers };
  let body: BodyInit | undefined;
  if (opts.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.json);
  } else if (opts.raw !== undefined) {
    body = opts.raw;
  }
  const res = await fetch(apiUrl(path), {
    method,
    headers,
    body,
    credentials: "include",
  });
  if (!res.ok) {
    let message = res.statusText;
    let data: Record<string, unknown> | undefined;
    try {
      data = await res.json();
      if (data?.error) {
        message = String(data.error);
      }
    } catch {
      /* non-json error body */
    }
    throw new ApiError(res.status, message, data);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  if (opts.responseType === "arraybuffer") {
    return (await res.arrayBuffer()) as unknown as T;
  }
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return (await res.json()) as T;
  }
  return (await res.arrayBuffer()) as unknown as T;
}

export interface KiWarning {
  level: string;
  severity: string;
  summary: string;
}

// AP2 Phase 4 — KI-Chat (Run 1/2).
export interface KiAttachedFile {
  file_id: string;
  name: string;
  content_type?: string | null;
}

export interface KiChatReply {
  reply: string;
  turn_id: string;
  conversation_id: string;
}

// AP2 Phase 5 (v1.9) — a folder marked as a KI-Ordner (chat picker source).
export interface KiFolder {
  id: string;
  name: string;
  ki_ordner_type?: "ws_bound" | "free";
}

// AP2 Phase 5 — chat sidebar (conversations per KI-Ordner).
export interface KiConversation {
  conversation_id: string;
  last_at: string;
  message_count: number;
  title: string;
}

export interface KiMessage {
  id?: string | number;
  role: "user" | "assistant";
  content: string;
  created_at?: string;
}

// D-121 — editable intent system-list entry (projection over rag_entries).
export type KiIntent = "frage" | "anweisung" | "information" | "entscheidung";
export interface KiSystemEntry {
  id: string;
  content: string;
  kategorie?: string;
  art?: KiIntent;
  created_at?: string;
  revised_at?: string | null;
}

export const ryfeApi = {
  // --- Branding (#1/#8) ---
  getBranding: () => request<Branding>("GET", "/api/branding"),

  // --- Auth (#2) ---
  me: () =>
    request<{ user: RyfeUser; workspaces: RyfeWorkspace[] }>(
      "GET",
      "/api/auth/me",
    ),
  register: (email: string, password: string, workspaceName?: string) =>
    request<{ user: RyfeUser; workspace: { id: string; slug: string } }>(
      "POST",
      "/api/auth/register",
      { json: { email, password, workspaceName } },
    ),
  login: (email: string, password: string) =>
    request<{ user: RyfeUser }>("POST", "/api/auth/login", {
      json: { email, password },
    }),
  logout: () => request<void>("POST", "/api/auth/logout"),
  // V1.2 Block 3 — resolve a Freigabe-ID (exact match; 404 → ApiError).
  resolveShareId: (shareId: string) =>
    request<{ userId: string; label: string }>(
      "GET",
      `/api/auth/users/by-share-id/${encodeURIComponent(shareId)}`,
    ),

  // --- V1.2 Blocks 2/4: ACL on folders and files ---
  listFolderAcl: (ws: string, folderId: string) =>
    request<{ entries: AclEntry[]; pending?: PendingInvite[] }>(
      "GET",
      `/api/workspaces/${ws}/folders/${folderId}/acl`,
    ),
  setFolderAcl: (
    ws: string,
    folderId: string,
    entry: { principalType: string; principalId: string; role: string },
  ) =>
    request<{ id: string; role: string }>(
      "PUT",
      `/api/workspaces/${ws}/folders/${folderId}/acl`,
      { json: entry },
    ),
  removeFolderAcl: (
    ws: string,
    folderId: string,
    entry: { principalType: string; principalId: string },
  ) =>
    request<void>("DELETE", `/api/workspaces/${ws}/folders/${folderId}/acl`, {
      json: entry,
    }),
  listFileAcl: (ws: string, fileId: string) =>
    request<{ entries: AclEntry[]; pending?: PendingInvite[] }>(
      "GET",
      `/api/workspaces/${ws}/files/${fileId}/acl`,
    ),
  setFileAcl: (
    ws: string,
    fileId: string,
    entry: { principalType: string; principalId: string; role: string },
  ) =>
    request<{ id: string; role: string }>(
      "PUT",
      `/api/workspaces/${ws}/files/${fileId}/acl`,
      { json: entry },
    ),
  removeFileAcl: (
    ws: string,
    fileId: string,
    entry: { principalType: string; principalId: string },
  ) =>
    request<void>("DELETE", `/api/workspaces/${ws}/files/${fileId}/acl`, {
      json: entry,
    }),

  // --- R4: „Zuletzt" über alle Unterordner (guard-gefiltert) ---
  listRecentFiles: (ws: string) =>
    request<{ files: FileMeta[] }>("GET", `/api/workspaces/${ws}/recent-files`),
  // --- v1.8 AP-5 (P23): „Zuletzt verwendete Ordner" (guard-gefiltert) ---
  listRecentFolders: (ws: string) =>
    request<{ folders: RecentFolder[] }>(
      "GET",
      `/api/workspaces/${ws}/recent-folders`,
    ),

  // --- v1.8 AP-5 (P21): per-user file favorites ---
  listFavoriteIds: (ws: string) =>
    request<{ ids: string[] }>("GET", `/api/workspaces/${ws}/favorite-ids`),
  listFavorites: (ws: string) =>
    request<{ files: FileMeta[] }>("GET", `/api/workspaces/${ws}/favorites`),
  addFavorite: (ws: string, fileId: string) =>
    request<{ id: string; favorite: boolean }>(
      "PUT",
      `/api/workspaces/${ws}/files/${fileId}/favorite`,
    ),
  removeFavorite: (ws: string, fileId: string) =>
    request<void>("DELETE", `/api/workspaces/${ws}/files/${fileId}/favorite`),
  // R4 — flache, guard-gefilterte Datei-Liste eines Teilbaums („Ordner
  // ausblenden"). Ohne folderId: der ganze Workspace (ohne Trash/Archiv).
  listFilesFlat: (ws: string, folderId?: string) =>
    request<{ files: FileMeta[] }>(
      "GET",
      `/api/workspaces/${ws}/files-flat${
        folderId ? `?folderId=${folderId}` : ""
      }`,
    ),

  // --- Revision R3: zentrale Freigaben-Übersicht (guard-gefiltert) ---
  listShares: (ws: string) =>
    request<{
      entries: ShareOverviewEntry[];
      pending?: (PendingInvite & {
        target: {
          type: "file" | "folder";
          id: string;
          name: string;
          kind?: string;
          isRoot?: boolean;
        };
      })[];
    }>("GET", `/api/workspaces/${ws}/shares`),

  // --- V1.2 Block 5: cross-workspace "shared with me" listing ---
  sharedWithMe: () =>
    request<{ items: SharedItem[] }>("GET", "/api/shared-with-me"),

  // --- Feature #2 (System 1): workspace membership ---
  listMembers: (ws: string) =>
    request<{ members: WsMember[] }>("GET", `/api/workspaces/${ws}/members`),
  // AP3 — Workspace-Einstellungen: umbenennen (owner/admin).
  renameWorkspace: (ws: string, name: string) =>
    request<{ id: string; name: string; slug: string }>(
      "PATCH",
      `/api/workspaces/${ws}`,
      { json: { name } },
    ),
  // Rolle eines BESTEHENDEN Mitglieds ändern (neue kommen per Einladung, D-142).
  setMemberRole: (
    ws: string,
    email: string,
    role: "owner" | "admin" | "member" | "viewer",
  ) =>
    request<{ userId: string; role: string }>(
      "PUT",
      `/api/workspaces/${ws}/members`,
      { json: { email, role } },
    ),
  removeMember: (ws: string, userId: string) =>
    request<void>("DELETE", `/api/workspaces/${ws}/members/${userId}`),
  // Eigene offene WS-Einladungen (an die eigene E-Mail) + annehmen/ablehnen.
  myInvitations: () =>
    request<{ invitations: WsInvite[] }>("GET", "/api/me/invitations"),
  acceptInvitation: (id: string) =>
    request<{ workspace_id: string }>("POST", `/api/invitations/${id}/accept`),
  declineInvitation: (id: string) =>
    request<void>("POST", `/api/invitations/${id}/decline`),
  // Neues Mitglied einladen (Invite/Accept-Flow, kein Force-Join).
  inviteMember: (
    ws: string,
    email: string,
    role: "admin" | "member" | "viewer",
  ) =>
    request<{ id: string; signup_token?: string }>(
      "POST",
      `/api/workspaces/${ws}/invitations`,
      { json: { email, role } },
    ),

  // --- v1.8 Cluster B.1 (D-146): Workspaces auflisten + anlegen ---
  // Ein Nutzer kann mehrere Workspaces (Mandanten) besitzen/beitreten; der
  // Ersteller wird owner. Backend-Bootstrap wie bei der Registrierung.
  listWorkspaces: () =>
    request<{ workspaces: RyfeWorkspace[] }>("GET", "/api/workspaces"),
  createWorkspace: (name: string, slug?: string) =>
    request<{ id: string; slug: string; role: "owner" }>(
      "POST",
      "/api/workspaces",
      { json: { name, ...(slug ? { slug } : {}) } },
    ),

  // --- v1.8 Cluster B.5 (D-146): Privatbereich (Cross-WS-Aggregation) ---
  // Der persönliche, Workspace-übergreifende Überblick eines Nutzers — getrennt
  // vom einzelnen Workspace (Mandant).
  privateOverview: () =>
    request<PrivateOverview>("GET", "/api/private/overview"),
  // D-146 — die Cross-WS-Aggregation der Systemlisten (Projekte/Kontakte/
  // Lesezeichen/Aufgaben/Termine) für den Privatbereich.
  privateSystemlists: () =>
    request<PrivateSystemlists>("GET", "/api/private/systemlists"),
  privateTasks: (status?: string) =>
    request<{ tasks: PrivateTask[] }>(
      "GET",
      `/api/private/tasks${
        status ? `?status=${encodeURIComponent(status)}` : ""
      }`,
    ),

  // --- v1.8 WS-Plattform (D-111/D-132): groups as sub-scopes ---
  listGroups: (ws: string) =>
    request<{ groups: RyfeGroup[] }>("GET", `/api/workspaces/${ws}/groups`),
  createGroup: (ws: string, name: string) =>
    request<RyfeGroup>("POST", `/api/workspaces/${ws}/groups`, {
      json: { name },
    }),
  deleteGroup: (ws: string, groupId: string) =>
    request<void>("DELETE", `/api/workspaces/${ws}/groups/${groupId}`),
  listGroupMembers: (ws: string, groupId: string) =>
    request<{ members: GroupMember[] }>(
      "GET",
      `/api/workspaces/${ws}/groups/${groupId}/members`,
    ),
  addGroupMember: (
    ws: string,
    groupId: string,
    email: string,
    role: "admin" | "member" = "member",
  ) =>
    request<GroupMember>(
      "PUT",
      `/api/workspaces/${ws}/groups/${groupId}/members`,
      { json: { email, role } },
    ),
  removeGroupMember: (ws: string, groupId: string, userId: string) =>
    request<void>("DELETE", `/api/workspaces/${ws}/groups/${groupId}/members`, {
      json: { userId },
    }),

  // --- v1.8 AP-10 (D-128): capability-set („Einstellungen → Rechte") ---
  listCapabilities: (ws: string) =>
    request<{ grants: Capability[] }>(
      "GET",
      `/api/workspaces/${ws}/capabilities`,
    ),
  listCapabilityTypes: (ws: string) =>
    request<{ types: string[] }>(
      "GET",
      `/api/workspaces/${ws}/capability-types`,
    ),
  setCapability: (
    ws: string,
    grant: {
      groupId: string;
      listType: string;
      read?: boolean;
      submit?: boolean;
      rowEdit?: boolean;
      schemaEdit?: boolean;
      fieldEdit?: string[];
    },
  ) =>
    request<Capability>("PUT", `/api/workspaces/${ws}/capabilities`, {
      json: grant,
    }),
  removeCapability: (ws: string, groupId: string, listType: string) =>
    request<void>("DELETE", `/api/workspaces/${ws}/capabilities`, {
      json: { groupId, listType },
    }),

  // --- CC_05 Block 5: saved table-filter presets (per-user, scope-keyed) ---
  listSavedViews: (scope: string) =>
    request<{ views: SavedView[] }>(
      "GET",
      `/api/saved-views?scope=${encodeURIComponent(scope)}`,
    ),
  createSavedView: (scope: string, name: string, config: unknown) =>
    request<SavedView>("POST", "/api/saved-views", {
      json: { scope, name, config },
    }),
  deleteSavedView: (id: string) =>
    request<void>("DELETE", `/api/saved-views/${id}`),

  // --- CC_05 Block 8: list data (columns + rows) ---
  // CC_11-Revision — all readable list files of the workspace (any folder
  // depth); feeds the relation-target picker.
  listWorkspaceLists: (ws: string) =>
    request<{
      lists: {
        id: string;
        name: string;
        folder_id: string;
        // A3b (D-158/D-160) — der Systemlisten-Typ `<paket>.<liste>` (null bei
        // gewöhnlichen Listen); der CRM-Reiter-Container löst darüber die
        // Geschwister-Listen (contacts/companies/persons) auf.
        list_type?: string | null;
      }[];
    }>("GET", `/api/workspaces/${ws}/lists`),
  // D-146/Team — optionaler `group` verengt eine Systemliste server-seitig auf
  // genau diese Gruppe (Team-View: Dashboard zeigt nur die Team-Zeilen).
  getList: (ws: string, fileId: string, group?: string) =>
    request<ListSnapshot>(
      "GET",
      `/api/workspaces/${ws}/files/${fileId}/list${
        group ? `?group=${encodeURIComponent(group)}` : ""
      }`,
    ),
  // D-146/Team — „Forms & View je Team": die departmentalen Systemlisten (je
  // gruppen-gefiltert öffenbar) + die on-demand-Prozesse dieser Gruppe (die
  // instanziierbaren Vorlagen für „Aufgabe hinzufügen").
  groupTeamLists: (ws: string, groupId: string) =>
    request<TeamLists>(
      "GET",
      `/api/workspaces/${ws}/groups/${groupId}/team-lists`,
    ),
  // D-147 — ein Prozess inkl. `required_inputs` (für die Instanziierungs-Form).
  getProcess: (ws: string, id: string) =>
    request<ProcessDetail>("GET", `/api/workspaces/${ws}/processes/${id}`),
  // D-147 — „aus Vorlage erstellen": einzelne on-demand-Materialisierung (L2
  // standalone) bzw. Serie (L1). `body` trägt die Pflicht-Inputs als Felder
  // (subject_contact_id/project_id/…); der Server erzwingt deren Präsenz und
  // stempelt `group_id` (Team) über den Prozess.
  // AP1 — die instanziierbaren Prozesse (für „aus Prozess erstellen" in der
  // Aufgaben-Liste); optional auf on_demand und/oder eine Gruppe verengt.
  listProcesses: (
    ws: string,
    opts: { type?: string; groupId?: string | null } = {},
  ) => {
    const q = new URLSearchParams();
    if (opts.type) {
      q.set("type", opts.type);
    }
    if (opts.groupId) {
      q.set("groupId", opts.groupId);
    }
    const qs = q.toString();
    return request<{
      processes: {
        id: string;
        name: string;
        level: number | null;
        type?: string | null;
      }[];
    }>("GET", `/api/workspaces/${ws}/processes${qs ? `?${qs}` : ""}`);
  },
  materializeProcess: (ws: string, id: string, body: Record<string, unknown>) =>
    request<{ id: string }>(
      "POST",
      `/api/workspaces/${ws}/processes/${id}/materialize`,
      { json: body },
    ),
  materializeProcessSeries: (
    ws: string,
    id: string,
    body: Record<string, unknown>,
  ) =>
    request<{ tasks: { id: string }[] }>(
      "POST",
      `/api/workspaces/${ws}/processes/${id}/materialize-series`,
      { json: body },
    ),
  createListColumn: (
    ws: string,
    fileId: string,
    body: { name: string; type: string; position?: number; config?: unknown },
  ) =>
    request<ListColumn>(
      "POST",
      `/api/workspaces/${ws}/files/${fileId}/list/columns`,
      { json: body },
    ),
  updateListColumn: (
    ws: string,
    fileId: string,
    columnId: string,
    patch: {
      name?: string;
      type?: string;
      position?: number;
      config?: unknown;
    },
  ) =>
    request<ListColumn>(
      "PATCH",
      `/api/workspaces/${ws}/files/${fileId}/list/columns/${columnId}`,
      { json: patch },
    ),
  deleteListColumn: (ws: string, fileId: string, columnId: string) =>
    request<void>(
      "DELETE",
      `/api/workspaces/${ws}/files/${fileId}/list/columns/${columnId}`,
    ),
  // AP-6 (P7.2 / D-137) — backfill an auto-create column over existing rows.
  // 409 CONFIRM_REQUIRED (with `count`) when above the server threshold and
  // `confirm` is not set; the UI confirms and retries with confirm=true.
  backfillListColumn: (
    ws: string,
    fileId: string,
    columnId: string,
    confirm = false,
  ) =>
    request<{ missing: number; created: number }>(
      "POST",
      `/api/workspaces/${ws}/files/${fileId}/list/columns/${columnId}/backfill`,
      { json: { confirm } },
    ),
  createListRow: (
    ws: string,
    fileId: string,
    body: {
      data?: Record<string, unknown>;
      position?: number;
      // R4 — optionale Unterwert-Zeile unter einer bestehenden Zeile.
      parent_row_id?: string | null;
      // v1.8 AP-10 Inc 3b (D-140) — Ersteller-Gruppe (Systemlisten): die aktive
      // Gruppe bzw. eine manuelle Auswahl; weggelassen/null = WS-weiter Eintrag.
      group_id?: string | null;
    },
  ) =>
    request<ListRow>(
      "POST",
      `/api/workspaces/${ws}/files/${fileId}/list/rows`,
      { json: body },
    ),
  // R4 — Listen-Schalter „Unterwerte zulassen" (Listeneinstellungen).
  setListSettings: (
    ws: string,
    fileId: string,
    settings: { allow_subvalues: boolean },
  ) =>
    request<{ settings: { allow_subvalues?: boolean } }>(
      "PATCH",
      `/api/workspaces/${ws}/files/${fileId}/list/settings`,
      { json: settings },
    ),
  updateListRow: (
    ws: string,
    fileId: string,
    rowId: string,
    patch: {
      data?: Record<string, unknown>;
      position?: number;
      links?: Record<string, string[]>;
    },
  ) =>
    request<ListRow>(
      "PATCH",
      `/api/workspaces/${ws}/files/${fileId}/list/rows/${rowId}`,
      { json: patch },
    ),
  deleteListRow: (ws: string, fileId: string, rowId: string) =>
    request<void>(
      "DELETE",
      `/api/workspaces/${ws}/files/${fileId}/list/rows/${rowId}`,
    ),

  // --- A3c (D-158): addresses of a contact (row-detail subgrid) ---
  // The Kontakte-Systemliste's row id is the contact_id; the detail panel lists
  // and edits its addresses over the A3a CRM endpoints.
  listContactAddresses: (ws: string, contactId: string) =>
    request<{ addresses: Address[] }>(
      "GET",
      `/api/workspaces/${ws}/contacts/${contactId}/addresses`,
    ),
  addContactAddress: (
    ws: string,
    contactId: string,
    body: {
      kind?: string;
      street?: string;
      zip?: string;
      city?: string;
      country?: string;
      notes?: string;
    },
  ) =>
    request<Address>(
      "POST",
      `/api/workspaces/${ws}/contacts/${contactId}/addresses`,
      { json: body },
    ),
  updateAddress: (
    ws: string,
    addressId: string,
    patch: {
      kind?: string;
      street?: string;
      zip?: string;
      city?: string;
      country?: string;
      notes?: string;
    },
  ) =>
    request<Address>("PATCH", `/api/workspaces/${ws}/addresses/${addressId}`, {
      json: patch,
    }),
  deleteAddress: (ws: string, addressId: string) =>
    request<void>("DELETE", `/api/workspaces/${ws}/addresses/${addressId}`),

  // --- CC_11: list views + forms (D-59/D-66) ---
  listListViews: (ws: string, fileId: string) =>
    request<{ views: ListView[] }>(
      "GET",
      `/api/workspaces/${ws}/files/${fileId}/list/views`,
    ),
  createListView: (
    ws: string,
    fileId: string,
    body: { type?: string; name: string; config?: unknown; position?: number },
  ) =>
    request<ListView>(
      "POST",
      `/api/workspaces/${ws}/files/${fileId}/list/views`,
      { json: body },
    ),
  updateListView: (
    ws: string,
    fileId: string,
    viewId: string,
    patch: { name?: string; config?: unknown; position?: number },
  ) =>
    request<ListView>(
      "PATCH",
      `/api/workspaces/${ws}/files/${fileId}/list/views/${viewId}`,
      { json: patch },
    ),
  deleteListView: (ws: string, fileId: string, viewId: string) =>
    request<void>(
      "DELETE",
      `/api/workspaces/${ws}/files/${fileId}/list/views/${viewId}`,
    ),
  // Form endpoints by OPAQUE form id — public or auth'd, decided per form.
  getForm: (formId: string) =>
    request<FormDescriptor>("GET", `/api/forms/${formId}`),
  submitForm: (
    formId: string,
    body: {
      data: Record<string, unknown>;
      links?: Record<string, string[]>;
    },
  ) =>
    request<{ ok: true; row_id?: string }>(
      "POST",
      `/api/forms/${formId}/submit`,
      { json: body },
    ),
  // R4 — guard-gefilterte Live-Suche über die Ziel-Liste eines Relations-Feldes
  // (nur angemeldete, nicht-öffentliche Formulare; sonst 403 serverseitig).
  formRelationSearch: (formId: string, columnId: string, q = "") =>
    request<{ items: { row_id: string; display: string }[]; hasMore: boolean }>(
      "GET",
      `/api/forms/${formId}/relation-search?columnId=${encodeURIComponent(
        columnId,
      )}${q ? `&q=${encodeURIComponent(q)}` : ""}`,
    ),

  // --- CC_11 (D-64/D-65): relation search — id-only {row_id, display} ---
  relationSearch: (
    ws: string,
    fileId: string,
    columnId: string,
    opts: { q?: string; limit?: number; offset?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (opts.q) {
      qs.set("q", opts.q);
    }
    if (opts.limit) {
      qs.set("limit", String(opts.limit));
    }
    if (opts.offset) {
      qs.set("offset", String(opts.offset));
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<{
      items: { row_id: string; display: string }[];
      total: number;
      hasMore: boolean;
    }>(
      "GET",
      `/api/workspaces/${ws}/files/${fileId}/list/columns/${columnId}/relation-search${suffix}`,
    );
  },

  // --- CC_11 (D-71): row comments — one flat thread per row ---
  listRowComments: (ws: string, fileId: string, rowId: string) =>
    request<{ comments: RowComment[] }>(
      "GET",
      `/api/workspaces/${ws}/files/${fileId}/list/rows/${rowId}/comments`,
    ),
  createRowComment: (ws: string, fileId: string, rowId: string, body: string) =>
    request<RowComment>(
      "POST",
      `/api/workspaces/${ws}/files/${fileId}/list/rows/${rowId}/comments`,
      { json: { body } },
    ),
  updateRowComment: (
    ws: string,
    fileId: string,
    rowId: string,
    commentId: string,
    body: string,
  ) =>
    request<RowComment>(
      "PATCH",
      `/api/workspaces/${ws}/files/${fileId}/list/rows/${rowId}/comments/${commentId}`,
      { json: { body } },
    ),
  deleteRowComment: (
    ws: string,
    fileId: string,
    rowId: string,
    commentId: string,
  ) =>
    request<void>(
      "DELETE",
      `/api/workspaces/${ws}/files/${fileId}/list/rows/${rowId}/comments/${commentId}`,
    ),

  // --- CC_11-Revision (P8.2): invite by e-mail — existence-neutral ---
  inviteToFile: (
    ws: string,
    fileId: string,
    email: string,
    role: "viewer" | "editor",
  ) =>
    request<{ ok: true }>(
      "POST",
      `/api/workspaces/${ws}/files/${fileId}/invite`,
      { json: { email, role } },
    ),
  // R4 (Punkt 8.2) — eine ausstehende Einladung widerrufen.
  revokeInvite: (ws: string, fileId: string, email: string) =>
    request<void>("DELETE", `/api/workspaces/${ws}/files/${fileId}/invite`, {
      json: { email },
    }),
  // v1.6 — dieselbe Einladung per E-Mail für ORDNER (Bugfix).
  inviteToFolder: (
    ws: string,
    folderId: string,
    email: string,
    role: "viewer" | "editor",
  ) =>
    request<{ ok: true }>(
      "POST",
      `/api/workspaces/${ws}/folders/${folderId}/invite`,
      { json: { email, role } },
    ),
  revokeFolderInvite: (ws: string, folderId: string, email: string) =>
    request<void>(
      "DELETE",
      `/api/workspaces/${ws}/folders/${folderId}/invite`,
      {
        json: { email },
      },
    ),

  // --- CC_11 (D-62/D-63): templates + fork ---
  listTemplates: (ws: string) =>
    request<{ templates: TemplateFile[] }>(
      "GET",
      `/api/workspaces/${ws}/templates`,
    ),
  setFileTemplateRole: (ws: string, fileId: string, role: "template" | null) =>
    request<{ id: string; role: string | null }>(
      "POST",
      `/api/workspaces/${ws}/files/${fileId}/template`,
      { json: { role } },
    ),
  forkFile: (
    ws: string,
    fileId: string,
    body: {
      folderId: string;
      name?: string;
      seed?: boolean;
      // P12 — fork a template whose SOURCE lives in another workspace (the
      // caller's own templates) into a folder in `ws` (e.g. a shared foreign
      // folder). Omitted = same-workspace fork.
      sourceWorkspaceId?: string;
    },
  ) =>
    request<{ id: string; name: string; kind: string; folder_id: string }>(
      "POST",
      `/api/workspaces/${ws}/files/${fileId}/fork`,
      { json: body },
    ),

  // --- CC_11 (D-72): per-user global settings ---
  getSettings: () =>
    request<{ settings: UserSettings }>("GET", "/api/account/settings"),
  patchSettings: (settings: Partial<UserSettings>) =>
    request<{ settings: UserSettings }>("PATCH", "/api/account/settings", {
      json: { settings },
    }),

  // --- KI system-prompt slots (AP2·P3-4) ---
  getKiPrompt: (ws: string, level: string, refId: string) =>
    request<{ ki_system_prompt: string }>(
      "GET",
      `/api/workspaces/${ws}/ki/prompt?level=${encodeURIComponent(
        level,
      )}&ref_id=${encodeURIComponent(refId)}`,
    ),
  setKiPrompt: (
    ws: string,
    body: {
      level: string;
      ref_id: string;
      ki_system_prompt: string;
      context?: { ki_ordner_id?: string; lens_id?: string };
    },
  ) =>
    request<{ prompt: unknown; warnings: KiWarning[] }>(
      "PUT",
      `/api/workspaces/${ws}/ki/prompt`,
      { json: body },
    ),
  setKiFolderConfig: (
    ws: string,
    body: {
      ki_ordner_id: string;
      ki_ordner_type: "ws_bound" | "free";
      bound_workspace_id?: string | null;
      // AP2 Phase 5 — mark (default) or, with false, un-mark the KI-Ordner.
      is_ki_ordner?: boolean;
    },
  ) =>
    request<{
      folder: {
        id: string;
        ki_ordner_type: string;
        bound_workspace_id: string | null;
        is_ki_ordner: boolean;
      };
    }>("PUT", `/api/workspaces/${ws}/ki/folder-config`, { json: body }),

  // AP2 Phase 5 (v1.9) — the workspace's KI-Ordner (subtype), for the chat picker.
  listKiFolders: (ws: string) =>
    request<{ folders: KiFolder[] }>("GET", `/api/workspaces/${ws}/ki/folders`),

  // AP2 Phase 4 (Run 1/2) — one KI-Chat turn against the Dify chatflow. The
  // server persists the conversation (A-Pfad); a Dify failure surfaces as an
  // ApiError (502) the client renders as an error bubble (D-107, no auto-retry).
  kiChat: (
    ws: string,
    body: {
      ki_ordner_id: string;
      conversation_id: string;
      query: string;
      lens_id?: string;
      category?: string;
      file_id?: string;
      attached_files?: KiAttachedFile[];
    },
  ) =>
    request<KiChatReply>("POST", `/api/workspaces/${ws}/ki/chat`, {
      json: body,
    }),

  // AP2 Phase 5 — chat sidebar: list conversations of a KI-Ordner, and load one.
  listKiConversations: (ws: string, kiOrdnerId: string) =>
    request<{ conversations: KiConversation[] }>(
      "GET",
      `/api/workspaces/${ws}/ki/conversations?ki_ordner_id=${encodeURIComponent(
        kiOrdnerId,
      )}`,
    ),
  listKiMessages: (ws: string, kiOrdnerId: string, conversationId: string) =>
    request<{ messages: KiMessage[] }>(
      "GET",
      `/api/workspaces/${ws}/ki/messages?ki_ordner_id=${encodeURIComponent(
        kiOrdnerId,
      )}&conversation_id=${encodeURIComponent(conversationId)}`,
    ),

  // D-121 — editable KI-Ordner knowledge: intent system lists + messages/sessions.
  getKiSystemList: (ws: string, kiOrdnerId: string, art: KiIntent) =>
    request<{ art: KiIntent; entries: KiSystemEntry[] }>(
      "GET",
      `/api/workspaces/${ws}/ki/system-list?ki_ordner_id=${encodeURIComponent(
        kiOrdnerId,
      )}&art=${encodeURIComponent(art)}`,
    ),
  updateKiEntry: (
    ws: string,
    id: string,
    kiOrdnerId: string,
    content: string,
  ) =>
    request<{ entry: KiSystemEntry }>(
      "PATCH",
      `/api/workspaces/${ws}/ki/system-list/entry/${encodeURIComponent(id)}`,
      { json: { ki_ordner_id: kiOrdnerId, content } },
    ),
  deleteKiEntry: (ws: string, id: string, kiOrdnerId: string) =>
    request<void>(
      "DELETE",
      `/api/workspaces/${ws}/ki/system-list/entry/${encodeURIComponent(
        id,
      )}?ki_ordner_id=${encodeURIComponent(kiOrdnerId)}`,
    ),
  updateKiMessage: (
    ws: string,
    id: string,
    kiOrdnerId: string,
    content: string,
  ) =>
    request<{ message: { id: string; content: string } }>(
      "PATCH",
      `/api/workspaces/${ws}/ki/message/${encodeURIComponent(id)}`,
      { json: { ki_ordner_id: kiOrdnerId, content } },
    ),
  deleteKiMessage: (ws: string, id: string, kiOrdnerId: string) =>
    request<void>(
      "DELETE",
      `/api/workspaces/${ws}/ki/message/${encodeURIComponent(
        id,
      )}?ki_ordner_id=${encodeURIComponent(kiOrdnerId)}`,
    ),
  deleteKiConversation: (
    ws: string,
    kiOrdnerId: string,
    conversationId: string,
  ) =>
    request<{ deleted: number }>(
      "DELETE",
      `/api/workspaces/${ws}/ki/conversation?ki_ordner_id=${encodeURIComponent(
        kiOrdnerId,
      )}&conversation_id=${encodeURIComponent(conversationId)}`,
    ),

  // --- Files & folders (#3) ---
  getRoot: (ws: string) =>
    request<FolderListing>("GET", `/api/workspaces/${ws}/folders/root`),
  listFolder: (ws: string, folderId: string) =>
    request<FolderListing>("GET", `/api/workspaces/${ws}/folders/${folderId}`),
  createFolder: (ws: string, parentId: string, name: string) =>
    request<{ id: string; name: string }>(
      "POST",
      `/api/workspaces/${ws}/folders/${parentId}/folders`,
      { json: { name } },
    ),
  // Rename/move (metadata only) — Block 3.1.
  patchFile: (
    ws: string,
    fileId: string,
    patch: { name?: string; folderId?: string; mainFrameId?: string | null },
  ) =>
    request<{
      id: string;
      name: string;
      folder_id: string;
      main_frame_id: string | null;
    }>("PATCH", `/api/workspaces/${ws}/files/${fileId}`, { json: patch }),
  patchFolder: (
    ws: string,
    folderId: string,
    patch: { name?: string; parentId?: string },
  ) =>
    request<{ id: string; name: string; parent_id: string | null }>(
      "PATCH",
      `/api/workspaces/${ws}/folders/${folderId}`,
      { json: patch },
    ),
  // v1.3.0 Block 3 (D-19): "löschen" = ab in den Papierkorb. `permanent` ist
  // der Hard-Delete und greift nur innerhalb des Papierkorbs.
  deleteFolder: (ws: string, folderId: string, permanent = false) =>
    request<void>(
      "DELETE",
      `/api/workspaces/${ws}/folders/${folderId}${
        permanent ? "?permanent=1" : ""
      }`,
    ),
  archiveFolder: (ws: string, folderId: string) =>
    request<void>("POST", `/api/workspaces/${ws}/folders/${folderId}/archive`),
  restoreFolder: (ws: string, folderId: string) =>
    request<void>("POST", `/api/workspaces/${ws}/folders/${folderId}/restore`),
  createFile: (
    ws: string,
    folderId: string,
    name: string,
    kind: string,
    body: BodyInit,
    contentType = "application/json",
  ) =>
    request<FileMeta>(
      "POST",
      `/api/workspaces/${ws}/folders/${folderId}/files?kind=${encodeURIComponent(
        kind,
      )}&name=${encodeURIComponent(name)}`,
      { raw: body, headers: { "Content-Type": contentType } },
    ),
  getFile: (ws: string, fileId: string) =>
    request<FileMeta>("GET", `/api/workspaces/${ws}/files/${fileId}`),
  getFileContent: (ws: string, fileId: string) =>
    request<ArrayBuffer>(
      "GET",
      `/api/workspaces/${ws}/files/${fileId}/content`,
      { responseType: "arraybuffer" },
    ),
  // CC_05 Block 1 (D-29): `expectKind` opts into the server-side write-barrier —
  // the write is refused (409) if the stored file is a different kind. The board
  // editor passes 'board', the markdown editor 'md'; a mis-routed editor can then
  // never overwrite the wrong file.
  putFileContent: (
    ws: string,
    fileId: string,
    body: BodyInit,
    contentType = "application/json",
    expectKind?: string,
  ) =>
    request<{ id: string; size_bytes: number }>(
      "PUT",
      `/api/workspaces/${ws}/files/${fileId}/content${
        expectKind ? `?expectKind=${encodeURIComponent(expectKind)}` : ""
      }`,
      { raw: body, headers: { "Content-Type": contentType } },
    ),
  deleteFile: (ws: string, fileId: string, permanent = false) =>
    request<void>(
      "DELETE",
      `/api/workspaces/${ws}/files/${fileId}${permanent ? "?permanent=1" : ""}`,
    ),
  archiveFile: (ws: string, fileId: string) =>
    request<void>("POST", `/api/workspaces/${ws}/files/${fileId}/archive`),
  restoreFile: (ws: string, fileId: string) =>
    request<void>("POST", `/api/workspaces/${ws}/files/${fileId}/restore`),

  // --- Share (#4) ---
  createShare: (
    ws: string,
    fileId: string,
    ciphertext: string,
    iv: string,
    expiresInDays?: number,
  ) =>
    request<{ slug: string; expires_at: string | null }>(
      "POST",
      `/api/workspaces/${ws}/files/${fileId}/share`,
      { json: { ciphertext, iv, expiresInDays } },
    ),
  getShare: (slug: string) =>
    request<{ ciphertext: string; iv: string; expires_at: string | null }>(
      "GET",
      `/api/share/${slug}`,
    ),

  // --- Account (#2) ---
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>("POST", "/api/account/password", {
      json: { currentPassword, newPassword },
    }),

  // --- MCP token management (Block 4) ---
  createMcpToken: (
    ws: string,
    folderId: string,
    body: { name: string; role: AclRole; expiresInDays?: number | null },
  ) =>
    request<{
      id: string;
      token: string;
      role: AclRole;
      expires_at: string | null;
    }>("POST", `/api/workspaces/${ws}/folders/${folderId}/mcp-tokens`, {
      json: body,
    }),
  listMcpTokens: (ws: string, includeArchived = false) =>
    request<{ tokens: McpToken[] }>(
      "GET",
      `/api/workspaces/${ws}/mcp-tokens${
        includeArchived ? "?includeArchived=1" : ""
      }`,
    ),
  patchMcpToken: (
    ws: string,
    tokenId: string,
    patch: {
      name?: string;
      role?: AclRole;
      expiresInDays?: number | null;
      // v1.6 / D-KI (AP1) — reversible archive.
      archived?: boolean;
    },
  ) =>
    request<McpToken>("PATCH", `/api/workspaces/${ws}/mcp-tokens/${tokenId}`, {
      json: patch,
    }),
  deleteMcpToken: (ws: string, tokenId: string) =>
    request<void>("DELETE", `/api/workspaces/${ws}/mcp-tokens/${tokenId}`),

  // --- v1.6 / D-KI (AP4): shared columns on „Meine Dateien" ---
  // v1.7 — folder-scoped: `folderId` is the folder currently open in „Meine
  // Dateien"; the server returns the columns visible there (that folder + its
  // ancestors). Omitting it means the root (columns visible everywhere).
  listFileColumns: (ws: string, folderId?: string) =>
    request<{ columns: FileColumn[] }>(
      "GET",
      `/api/workspaces/${ws}/file-columns${
        folderId ? `?folderId=${folderId}` : ""
      }`,
    ),
  addFileColumn: (
    ws: string,
    body: {
      name: string;
      type?: string;
      config?: Record<string, unknown>;
      private?: boolean;
      // v1.7 — the folder to scope the new column to (its subtree). Omit for root.
      folderId?: string;
    },
  ) =>
    request<FileColumn>("POST", `/api/workspaces/${ws}/file-columns`, {
      json: body,
    }),
  updateFileColumn: (
    ws: string,
    colId: string,
    patch: {
      name?: string;
      config?: Record<string, unknown>;
      // AP-4 (P11.1) — privat/nicht-privat nachträglich umschaltbar.
      private?: boolean;
    },
  ) =>
    request<FileColumn>(
      "PATCH",
      `/api/workspaces/${ws}/file-columns/${colId}`,
      { json: patch },
    ),
  deleteFileColumn: (ws: string, colId: string) =>
    request<void>("DELETE", `/api/workspaces/${ws}/file-columns/${colId}`),
  setFileColumnValue: (
    ws: string,
    fileId: string,
    colId: string,
    value: unknown,
  ) =>
    request<void>(
      "PUT",
      `/api/workspaces/${ws}/files/${fileId}/user-columns/${colId}`,
      { json: { value } },
    ),
  getFileColumnValues: (ws: string, fileIds: string[], folderId?: string) =>
    request<{ values: Record<string, Record<string, unknown>> }>(
      "GET",
      `/api/workspaces/${ws}/file-column-values?fileIds=${fileIds.join(",")}${
        folderId ? `&folderId=${folderId}` : ""
      }`,
    ),

  // --- v1.6 / D-KI (AP-KI-Tags): per-document category tag + metadata note ---
  getFileMetadata: (ws: string, fileId: string) =>
    request<DocumentMetadata>(
      "GET",
      `/api/workspaces/${ws}/files/${fileId}/metadata`,
    ),
  setFileMetadata: (
    ws: string,
    fileId: string,
    body: { category?: string | null; metadata_md?: string },
  ) =>
    request<{ file_id: string; category: string | null; updated_at: string }>(
      "PUT",
      `/api/workspaces/${ws}/files/${fileId}/metadata`,
      { json: body },
    ),
  deleteFileMetadata: (ws: string, fileId: string) =>
    request<void>("DELETE", `/api/workspaces/${ws}/files/${fileId}/metadata`),
  getFileCategories: (ws: string, fileIds: string[]) =>
    request<{ metadata: Record<string, FileCategory> }>(
      "GET",
      `/api/workspaces/${ws}/file-metadata?fileIds=${fileIds.join(",")}`,
    ),

  // --- v1.6 / D-KI (AP3): the "Lenses" system list ---
  getLenses: (ws: string) =>
    request<{ fileId: string; columns: ListColumn[]; rows: ListRow[] }>(
      "GET",
      `/api/workspaces/${ws}/system-lists/lenses`,
    ),
  addLensColumn: (
    ws: string,
    body: { name: string; type?: string; config?: Record<string, unknown> },
  ) =>
    request<ListColumn>(
      "POST",
      `/api/workspaces/${ws}/system-lists/lenses/columns`,
      { json: body },
    ),
  deleteLensColumn: (ws: string, colId: string) =>
    request<void>(
      "DELETE",
      `/api/workspaces/${ws}/system-lists/lenses/columns/${colId}`,
    ),
  createLens: (
    ws: string,
    body: { data: Record<string, unknown>; private?: boolean },
  ) =>
    request<ListRow>("POST", `/api/workspaces/${ws}/system-lists/lenses/rows`, {
      json: body,
    }),
  updateLens: (
    ws: string,
    rowId: string,
    body: { data?: Record<string, unknown>; private?: boolean },
  ) =>
    request<ListRow>(
      "PATCH",
      `/api/workspaces/${ws}/system-lists/lenses/rows/${rowId}`,
      { json: body },
    ),
  deleteLens: (ws: string, rowId: string) =>
    request<void>(
      "DELETE",
      `/api/workspaces/${ws}/system-lists/lenses/rows/${rowId}`,
    ),

  // --- Element library (Block 5) ---
  getLibrary: (ws: string, folderId?: string) =>
    request<LibraryListing>(
      "GET",
      folderId
        ? `/api/workspaces/${ws}/library/folders/${folderId}`
        : `/api/workspaces/${ws}/library`,
    ),
  createLibraryFolder: (
    ws: string,
    body: { name: string; parentId?: string | null },
  ) =>
    request<{ id: string; name: string }>(
      "POST",
      `/api/workspaces/${ws}/library/folders`,
      { json: body },
    ),
  patchLibraryFolder: (
    ws: string,
    folderId: string,
    patch: { name?: string; parentId?: string | null },
  ) =>
    request<{ id: string; name: string; parent_id: string | null }>(
      "PATCH",
      `/api/workspaces/${ws}/library/folders/${folderId}`,
      { json: patch },
    ),
  deleteLibraryFolder: (ws: string, folderId: string) =>
    request<void>(
      "DELETE",
      `/api/workspaces/${ws}/library/folders/${folderId}`,
    ),
  getLibraryItem: (ws: string, itemId: string) =>
    request<LibraryItemFull>(
      "GET",
      `/api/workspaces/${ws}/library/items/${itemId}`,
    ),
  patchLibraryItem: (
    ws: string,
    itemId: string,
    patch: { name?: string; folderId?: string | null },
  ) =>
    request<{ id: string; name: string; folder_id: string | null }>(
      "PATCH",
      `/api/workspaces/${ws}/library/items/${itemId}`,
      { json: patch },
    ),
  deleteLibraryItem: (ws: string, itemId: string) =>
    request<void>("DELETE", `/api/workspaces/${ws}/library/items/${itemId}`),
  // v1.3.0 Block 2 (D-17) — store a scene selection as an own library element.
  // `folderId` is optional; omitting it files into "Meine Elemente".
  createLibraryItemFromSelection: (
    ws: string,
    name: string,
    elements: unknown[],
    folderId?: string,
  ) =>
    request<LibraryItemRow>("POST", `/api/workspaces/${ws}/library/items`, {
      json: { name, elements, folderId },
    }),
};

export type RyfeApi = typeof ryfeApi;
